import PermissionAwareExportButton from "#src/batteries-included-components/Buttons/PermissionAwareExportButton/PermissionAwareExportButton";
import { AssetActivityLogFiltersType } from "#src/batteries-included-components/FilterAreas/AssetActivityLogFilterAreas/AssetActivityLogFilterAreas";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import {
  useAssetResourceQuery,
  useExportActivityLogs,
  useListActivityLogs,
} from "#src/components/hooks/adapters/useActivityLogs";
import { useTableSortingAndPagination } from "#src/components/Redux/reducers/tableStateReducer";
import { useHasPermission } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { getLookupLink } from "#src/utils/display";
import {
  ArrayDataDisplay,
  Button,
  DataTable,
  DataTablePanel,
  type HeaderType,
  Icon,
  Link,
  StorageKeys,
  TextDataDisplay,
} from "@validereinc/common-components";
import {
  ActivityAction,
  ActivityAdapter,
  ActivityResource,
  ActivitySchema,
  type ActivityType,
  type AssetActivityLogAssetType,
  AssetType,
  DeviceType,
  EquipmentType,
  FlowType,
  SortDirection,
  UserTypeEnum,
  type UserWithGroupsType,
} from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import { isEmpty } from "es-toolkit/compat";
import React, { useMemo, useState } from "react";
import {
  getActionForDisplay,
  getListOfChangedAttributes,
} from "./ActivityLogsPage.helpers";

import { ActivityLogsDrawer } from "#src/batteries-included-components/Drawers/ActivityLogsDrawer";
import { useListUsers } from "#src/components/hooks/adapters/useUsers";

const activityModelKeys = ActivitySchema.keyof().Enum;

export const AssetActivityLogTablePanel = ({
  viewConfigStorageKey,
  tableStateStorageKey,
  assetType,
}: Pick<StorageKeys, "viewConfigStorageKey" | "tableStateStorageKey"> & {
  assetType: AssetActivityLogAssetType;
}) => {
  const [viewFilters] = useStickyState<AssetActivityLogFiltersType>(
    {} as AssetActivityLogFiltersType,
    viewConfigStorageKey
  );
  const { localize } = useLocalization();
  const [selectedLog, setSelectedLog] = useState<ActivityType | null>(null);

  const { resource_id, action, logged_by, logged_at } = viewFilters;

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "timestamp",
      sortDirection: SortDirection.DESCENDING,
      pageSize: 25,
    },
    viewFilters,
    tableStateStorageKey
  );

  const [selectedActivities, setSelectedActivities] = useState<
    Record<string, ActivityType>
  >({});
  const [canExportPermission] = useHasPermission({
    $and: ["reports:read", "reports:write"],
  });

  const viewFiltersToApply = {
    ...(resource_id && { resource_id }),
    ...(action && { action }),
    ...(logged_by && ({ author_id: logged_by } as any)),
    ...(logged_at && ({ timestamp: logged_at } as any)),
  };

  const activitiesQueryPayload = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: {
      ...viewFiltersToApply,
    },
    meta: { resourceType: assetType },
  } satisfies Parameters<typeof ActivityAdapter.getList>[0];

  const { data: activitiesData, isLoading: areActivityLogsLoading } =
    useListActivityLogs(activitiesQueryPayload);

  const activities = activitiesData?.data;

  const processActivities = useMemo(() => {
    const uniqueUserIds = new Set<string>();
    const uniqueResourceIds = new Set<string>();

    activities?.forEach((activity: ActivityType) => {
      uniqueUserIds.add(activity.author_id);
      if (activity.resource_id) {
        uniqueResourceIds.add(activity.resource_id);
      }
    });

    const uniqueUserIdsArray = Array.from(uniqueUserIds);
    const uniqueResourceIdsArray = Array.from(uniqueResourceIds);

    return {
      uniqueUserIdsArray,
      uniqueResourceIdsArray,
    };
  }, [activities, assetType]);

  const { uniqueUserIdsArray, uniqueResourceIdsArray } = processActivities;

  const { data: usersQueryData, isLoading: isLoadingUsers } = useListUsers(
    {
      filters: {
        id: uniqueUserIdsArray,
        type: [UserTypeEnum.human, UserTypeEnum.machine],
      },
    },
    {
      enabled: Boolean(uniqueUserIdsArray.length),
    }
  );
  const users = usersQueryData?.data ?? [];
  const userIdsToUserDetailsMap = useMemo(() => {
    return Object.fromEntries(users.map((user) => [user.id, user])) as Record<
      string,
      UserWithGroupsType
    >;
  }, [users]);

  const { resourceIdsToResourceDetailsMap, isLoadingResources } =
    useAssetResourceQuery({
      assetType,
      resourceIds: uniqueResourceIdsArray,
    });

  const exportActivityLogPayload = {
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: {
      ...(!isEmpty(selectedActivities)
        ? { id: Object.keys(selectedActivities) }
        : {}),
      ...viewFiltersToApply,
    },
    meta: { resourceType: assetType, period: "" },
  } satisfies Parameters<typeof ActivityAdapter.exportList>[0];

  const { mutate: handleExport, isLoading: isExporting } =
    useExportActivityLogs(exportActivityLogPayload);

  const localizedAssetText = toTitleCaseString(localize(assetType));
  const localizedFacilityText = toTitleCaseString(localize(AssetType.FACILITY));
  const isLoading =
    areActivityLogsLoading ||
    (Boolean(uniqueUserIdsArray.length) && isLoadingUsers) ||
    (Boolean(uniqueResourceIdsArray.length) && isLoadingResources);

  const headers = useMemo<Array<HeaderType<ActivityType>>>(
    () => [
      {
        key: activityModelKeys.action,
        label: "Type",
        renderComponent: ({ item }) => {
          if (!item) {
            return null;
          }

          const transformedAction = getActionForDisplay(item);
          const actionLabel = toTitleCaseString(
            `${localizedAssetText} ${transformedAction}`,
            { intent: "ui" }
          );

          return (
            <Link
              label={actionLabel}
              onClick={() => setSelectedLog(item)}
              isLoading={isLoading}
            />
          );
        },
      },
      {
        label: "Updated Attributes",
        key: "updated_attributes",
        renderComponent: ({ item }) => (
          <ArrayDataDisplay value={getListOfChangedAttributes(item) ?? []} />
        ),
      },
      ...(assetType === ActivityResource.FACILITY
        ? [
            {
              label: `${localizedFacilityText} ID`,
              key: activityModelKeys.resource_id,
              renderComponent: ({ item }) => {
                return <TextDataDisplay value={item.resource_id} />;
              },
            },
          ]
        : []),
      ...([
        ActivityResource.FLOW,
        ActivityResource.EQUIPMENT,
        ActivityResource.DEVICE,
      ].includes(assetType)
        ? [
            {
              label: `${localizedFacilityText} Name`,
              key: activityModelKeys.resource_id,
              renderComponent: ({ item }) => {
                const facility =
                  (
                    resourceIdsToResourceDetailsMap[item.resource_id] as
                      | EquipmentType
                      | DeviceType
                  )?.facility ||
                  (
                    resourceIdsToResourceDetailsMap[
                      item.resource_id
                    ] as FlowType
                  )?.associated_facility;
                const facilityName = facility?.name;
                const facilityId = facility?.id;

                return facilityId && facilityName
                  ? getLookupLink(facilityId, facilityName, AssetType.FACILITY)
                  : "-";
              },
            },
          ]
        : []),
      {
        label: `${localizedAssetText} Name`,
        key: "name",
        renderComponent: ({ item }) => {
          const isResourceDeleted = item.action === ActivityAction.DELETE;
          const isResourceCreated = item.action === ActivityAction.CREATE;
          const resourceName = isResourceDeleted
            ? (item?.before as { name: string })?.name
            : isResourceCreated
              ? (item?.after as { name: string })?.name
              : resourceIdsToResourceDetailsMap[item.resource_id]?.name;
          return resourceName
            ? getLookupLink(item.resource_id, resourceName, assetType)
            : "-";
        },
      },
      {
        key: activityModelKeys.author_id,
        label: "Logged By",
        renderComponent: ({ item }) => {
          const authorId = item.author_id;
          const authorName = userIdsToUserDetailsMap[authorId]?.name ?? "-";
          return (
            <UserDataDisplay
              id={authorId}
              name={authorName}
              isLoading={isLoading}
            />
          );
        },
      },
      {
        key: activityModelKeys.timestamp,
        label: "Logged At",
        isSortable: true,
        renderComponent: ({ item }) => (
          <DataTable.DataRow.DateCell
            value={item[activityModelKeys.timestamp]}
            withTime
            isLoading={isLoading}
          />
        ),
      },
    ],
    [userIdsToUserDetailsMap, resourceIdsToResourceDetailsMap]
  );

  const actionRow = [
    <PermissionAwareExportButton
      key="export"
      onClick={handleExport}
      isExporting={isExporting}
    />,
  ];

  return (
    <>
      <DataTablePanel
        panelProps={{
          title: `${localizedAssetText} Change Log`,
          actionRow,
        }}
        dataTableProps={{
          headers,
          items: activities ?? [],
          isLoading,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: activitiesData?.total_entries,
            pageSizeText: "logs per page",
          },
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
          selected: selectedActivities,
          onSelectionChange: setSelectedActivities,
          getItemId: (item) => item.id,
          getItemBulkActions: ({ buttonProps }) => [
            canExportPermission ? (
              <Button
                {...buttonProps}
                key="export"
                variant="outline"
                slotLeft={<Icon variant="arrow-square-out" />}
                isLoading={isExporting}
                onClick={() => handleExport()}
              >
                Export
              </Button>
            ) : null,
          ],
        }}
      />
      <ActivityLogsDrawer
        logDetails={selectedLog}
        onClose={() => setSelectedLog(null)}
      />
    </>
  );
};
